/**
 * Hearts game view.
 *
 * Reuses the same generic primitives as the Sheng Ji view:
 *   - `CircleTable` lays out seats around the table.
 *   - `Hand` renders the player's cards as a scrollable, sortable fan.
 *   - `PlayingCard` renders individual cards with an optional decorator.
 *
 * Game-specific bits (the per-seat scorecard, the passing UI, the
 * cumulative-score panel) are defined here.
 */

import { useMemo, useState } from "react";
import { lerpCardWidth, useResetOnChange, useWindowWidth } from "../../lib/uiHooks.js";
import type {
  HeartsAction,
  HeartsPublicState,
} from "../../../../shared/games/hearts.js";
import type { CardId } from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard, type CardDecorator } from "../../components/PlayingCard.js";

interface HeartsGameProps {
  state: HeartsPublicState;
  yourHand: CardId[];
  yourStaged: CardId[];          // cards staged to pass (passing phase)
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

/**
 * Hearts per-card overlay: the Q♠ gets a red stripe (13 points to whoever
 * captures it); each heart gets a small heart icon at the top-left (1
 * point each). Left-edge so they remain visible in the fanned hand.
 */
export function heartsDecorator(cardId: CardId): CardDecorator {
  if (cardId === "QS") {
    // Stripe down the left edge; the "13" badge sits at the BOTTOM of
    // the stripe so it doesn't collide with the suit glyph in the
    // top-left corner of the card.
    return (
      <g>
        <rect x={4} y={28} width={5} height={84} rx={2.5} fill="#c81818" />
        <circle cx={6.5} cy={120} r={7} fill="#c81818" />
        <text x={6.5} y={123.5} fontSize={9} textAnchor="middle" fill="#fff" fontWeight={700}>
          13
        </text>
      </g>
    );
  }
  if (cardId.length === 2 && cardId[1] === "H") {
    return <rect x={4} y={40} width={5} height={60} rx={2.5} fill="#c81818" />;
  }
  return null;
}


export function HeartsGame({
  state,
  yourHand,
  yourStaged,
  yourSeat,
  send,
  mobile = false,
}: HeartsGameProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Scoreboard visibility — open by default on desktop, closed on mobile so
  // it doesn't crowd the centre seat. The user can toggle either way.
  const [scoresOpen, setScoresOpen] = useState(!mobile);

  // Reset selection on phase/trick changes.
  const phaseKey = `${state.phase}:${state.currentTrickPlays.length}:${state.completedTricks.length}`;
  useResetOnChange(phaseKey, () => setSelected(new Set()));

  const windowWidth = useWindowWidth();
  const playZoneCardWidth = mobile ? 78 : lerpCardWidth(windowWidth, 90, 132);

  // Legal subset for hand dimming during the play phase.
  const legal = useMemo(() => {
    if (yourSeat === null) return new Set<CardId>();
    if (state.phase !== "play") return new Set(yourHand);
    if (state.currentSeat !== yourSeat) return new Set<CardId>(); // not your turn
    return computeHeartsLegal(yourHand, state, yourSeat);
  }, [yourHand, state, yourSeat]);

  const selectedCardIds = useMemo<CardId[]>(
    () => [...selected].map((i) => yourHand[i]!).filter(Boolean),
    [selected, yourHand]
  );

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div className="game-view">
      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <HeartsSeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            playZoneCardWidth={playZoneCardWidth}
          />
        )}
      >
        <HeartsCenter state={state} />
      </CircleTable>

      <button
        className="scoreboard-toggle"
        onClick={() => setScoresOpen((v) => !v)}
        aria-pressed={scoresOpen}
      >
        {scoresOpen ? "Hide scores" : "Show scores"}
      </button>
      {scoresOpen && <HeartsScorePanel state={state} yourSeat={yourSeat} />}

      {yourSeat !== null && state.phase === "passing" && (
        <PassingActionBar
          stagedCount={yourStaged.length + selected.size}
          selectedCards={selectedCardIds}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {yourSeat !== null && state.phase === "play" && state.currentSeat === yourSeat && (
        <PlayActionBar
          selectedCards={selectedCardIds}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {yourSeat !== null && state.phase === "round_end" && (
        <ReadyActionBar send={send} />
      )}

      {yourSeat !== null && (
        <Hand
          cards={yourHand}
          legal={state.phase === "play" ? legal : undefined}
          selected={selected}
          onToggle={toggle}
          decoratorOf={heartsDecorator}
          mobile={mobile}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat view
// ---------------------------------------------------------------------------

function HeartsSeatView({
  seat,
  state,
  yourSeat,
  playZoneCardWidth,
}: {
  seat: number;
  state: HeartsPublicState;
  yourSeat: number | null;
  playZoneCardWidth: number;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const isTurn = state.phase === "play" && state.currentSeat === seat;
  const trickPlay = state.currentTrickPlays.find((tp) => tp.seat === seat);
  const isWinning =
    state.currentTrickPlays.length > 0 &&
    state.currentTrickPlays[state.currentTrickWinningIndex]?.seat === seat;
  const isPassing = state.phase === "passing";
  return (
    <>
      <div
        className={[
          "nameplate",
          isMe ? "mine" : "opponent",
          isTurn ? "turn" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">total {p.totalScore}</span>
          {p.roundPoints > 0 && <span className="rank-line">+{p.roundPoints} this round</span>}
          {isPassing && (
            <span className="rank-line">{p.hasPassed ? "passed ✓" : "passing…"}</span>
          )}
        </div>
      </div>
      <div className={`play-zone ${isWinning ? "winning" : ""}`}>
        {isWinning && <div className="winning-badge">WINNING</div>}
        {trickPlay && (
          <div className="play-card-wrapper" style={{ position: "relative", zIndex: 1 }}>
            <PlayingCard
              card={trickPlay.card}
              width={playZoneCardWidth}
              decorator={heartsDecorator(trickPlay.card)}
            />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Centre pile
// ---------------------------------------------------------------------------

function HeartsCenter({ state }: { state: HeartsPublicState }) {
  if (state.phase === "passing") {
    const direction = state.passDirection;
    if (direction === "hold") {
      return <div className="center-pile"><span className="label">No pass this round</span></div>;
    }
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Passing 3 cards</span>
        <span className="points" style={{ fontSize: 18 }}>{direction}</span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    const moon = state.lastMoonShooter;
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Round complete</span>
        {moon !== null ? (
          <span className="points" style={{ fontSize: 18 }}>
            🌙 {state.players[moon]?.displayName ?? "?"} shot the moon!
          </span>
        ) : (
          <span className="points" style={{ fontSize: 16 }}>
            {state.players.map((p) => `${p.displayName} ${p.roundPoints}`).join(" · ")}
          </span>
        )}
      </div>
    );
  }
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Match complete</span>
        <span className="points">Game over</span>
      </div>
    );
  }
  // play phase
  if (state.currentTrickPlays.length === state.players.length && state.completedTricks.length > 0) {
    const last = state.completedTricks[state.completedTricks.length - 1]!;
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
        <span className="label">Trick won by</span>
        <span className="points" style={{ fontSize: 20 }}>{state.players[last.winner]?.displayName ?? "?"}</span>
        <span className="label">+{last.points} pts</span>
      </div>
    );
  }
  if (state.currentSeat >= 0) {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
        <span className="label">Waiting on</span>
        <span className="points" style={{ fontSize: 20 }}>{state.players[state.currentSeat]?.displayName ?? "?"}</span>
        {!state.heartsBroken && <span className="label" style={{ fontSize: 11 }}>hearts not broken</span>}
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Score panel (replaces ScoreBar) — simple table of cumulative scores
// ---------------------------------------------------------------------------

function HeartsScorePanel({
  state,
  yourSeat,
}: {
  state: HeartsPublicState;
  yourSeat: number | null;
}) {
  return (
    <div className="hearts-score-panel">
      <div className="hearts-score-header">
        <span className="muted">Round {state.roundNumber}</span>
        {state.phase === "passing" && (
          <span className="muted" style={{ marginLeft: 8 }}>
            pass {state.passDirection}
          </span>
        )}
      </div>
      <table className="hearts-score-table">
        <thead>
          <tr>
            <th>Player</th>
            <th style={{ textAlign: "right" }}>Round</th>
            <th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {state.players.map((p) => (
            <tr key={p.seat} className={p.seat === yourSeat ? "you" : ""}>
              <td>{p.displayName}</td>
              <td style={{ textAlign: "right" }}>{p.roundPoints}</td>
              <td style={{ textAlign: "right" }}>{p.totalScore}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action bars
// ---------------------------------------------------------------------------

function PassingActionBar({
  stagedCount,
  selectedCards,
  send,
  onAfterSubmit,
}: {
  stagedCount: number;
  selectedCards: CardId[];
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  const ok = selectedCards.length === 3;
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!ok}
        onClick={() => {
          send({ t: "action", action: { kind: "hearts", payload: { type: "pass", cards: selectedCards } } });
          onAfterSubmit();
        }}
      >
        {stagedCount >= 3 ? "Passed ✓" : `Pass ${selectedCards.length}/3 cards`}
      </button>
    </div>
  );
}

function PlayActionBar({
  selectedCards,
  send,
  onAfterSubmit,
}: {
  selectedCards: CardId[];
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  const ok = selectedCards.length === 1;
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!ok}
        onClick={() => {
          send({ t: "action", action: { kind: "hearts", payload: { type: "play", card: selectedCards[0]! } } });
          onAfterSubmit();
        }}
      >
        Play card
      </button>
    </div>
  );
}

function ReadyActionBar({ send }: { send: (m: ClientMsg) => void }) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        onClick={() => send({ t: "action", action: { kind: "hearts", payload: { type: "ready_next_round" } } })}
      >
        Ready for next round
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client-side legality hint
// ---------------------------------------------------------------------------

function computeHeartsLegal(
  hand: CardId[],
  state: HeartsPublicState,
  _yourSeat: number
): Set<CardId> {
  const out = new Set<CardId>();
  const trick = state.currentTrickPlays;
  if (trick.length === 0) {
    // Leading.
    if (state.isFirstTrick) {
      // Must play 2♣ if held.
      if (hand.includes("2C")) {
        out.add("2C");
        return out;
      }
      // Otherwise any non-point, non-heart card.
      for (const c of hand) {
        if (c[1] === "H") continue;
        if (c === "QS") continue;
        out.add(c);
      }
      return out;
    }
    if (state.heartsBroken) {
      for (const c of hand) out.add(c);
      return out;
    }
    // Hearts not broken: only non-hearts unless hand is all hearts.
    const nonHearts = hand.filter((c) => c[1] !== "H");
    if (nonHearts.length > 0) {
      for (const c of nonHearts) out.add(c);
    } else {
      for (const c of hand) out.add(c);
    }
    return out;
  }
  // Following.
  const ledSuit = trick[0]!.card[1];
  const inSuit = hand.filter((c) => c[1] === ledSuit);
  if (inSuit.length > 0) {
    for (const c of inSuit) out.add(c);
    return out;
  }
  // Void — discard. On first trick, no hearts / no Q♠ unless forced.
  if (state.isFirstTrick) {
    const safe = hand.filter((c) => c[1] !== "H" && c !== "QS");
    if (safe.length > 0) {
      for (const c of safe) out.add(c);
      return out;
    }
  }
  for (const c of hand) out.add(c);
  return out;
}
